import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/Card';
import { Rejection } from '../components/Rejection';
import { PageHeader } from '../components/Shell';
import { Waiting } from '../components/Waiting';
import { fullDate } from '../net/apiKeys';
import { ApiError } from '../net/client';
import {
  batchCell,
  emptyProvenance,
  exportBatch,
  exportFileName,
  exportFormat,
  exportKinds,
  exportLabels,
  type ExportKind,
  issueBatch,
  listAllBatches,
  quantityCell,
  sourceLabels,
  toProvenance,
  toQuantity,
  type CodeBatch,
  type ProvenanceFields,
} from '../net/codeBatches';
import { listAllProducts, type Product } from '../net/products';
import { useSession, useSessionState } from '../session/useSession';
import { chip, colors, fonts, radius, shell, type, verdict } from '../theme';
import { saveFile } from './saveFile';

/**
 * G4, `design/PortalCodes.dc.html`.
 *
 * Issue a batch on the left, every batch on the right, newest first. The
 * product list is every active product, first one chosen. Issue sends the
 * product, the quantity and whatever provenance was typed; a refusal keyed to
 * a drawn field lands under it, the rest in the rejection box at the top of
 * the card, as D10 does. A new batch is put first in the list and the form
 * keeps its product but clears the rest, so a second click cannot mint the
 * same run twice. Each row's Export menu asks the API for the CSV, the PDF
 * sheet on A4 or Letter, or the ZIP of images, and saves it, X3 and D34. Issuing is admin only; a member sees the list, the exports and a card in
 * place of the form. D19.
 */

type Load =
  | { status: 'loading' }
  | { status: 'failed'; error: unknown }
  | { status: 'loaded'; batches: CodeBatch[]; products: Product[] };

type FieldKey = 'productId' | 'quantity' | keyof ProvenanceFields;

type Problems = { general?: string; fields: Partial<Record<FieldKey, string>> };

const none: Problems = { fields: {} };

/** The one board that draws it: the machine API sample's ink. */
const sampleInk = '#D7DCE7';

const columns = '0.9fr 1.8fr 0.9fr 0.9fr 1fr 0.9fr';

/**
 * Issued is neutral and ingested brand, as drawn. Demo is not drawn, D36: the
 * quiet chip, and no Export, since a demo sample's address is on no label
 * (DEFERRED 4.20).
 */
const sourceTone: Record<string, { bg: string; fg: string }> = {
  issued: chip.neutral,
  ingested: chip.brand,
  demo: chip.muted,
};

export function CodeBatches() {
  const state = useSessionState();
  const isAdmin = state.status === 'signed_in' && state.user.role === 'org_admin';
  const { api } = useSession();
  const navigate = useNavigate();
  const [load, setLoad] = useState<Load>({ status: 'loading' });
  const [chosen, setChosen] = useState<string | null>(null);
  const [quantity, setQuantity] = useState('');
  const [provenance, setProvenance] = useState<ProvenanceFields>(emptyProvenance);
  const [issuing, setIssuing] = useState(false);
  const [problems, setProblems] = useState<Problems>(none);
  const [exporting, setExporting] = useState<{ batchId: string; kind: ExportKind } | null>(null);
  const [exportRefusal, setExportRefusal] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;
    Promise.all([listAllBatches(api, signal), listAllProducts(api, signal)]).then(
      ([batches, products]) => setLoad({ status: 'loaded', batches, products }),
      (error: unknown) => {
        if (!signal.aborted) setLoad({ status: 'failed', error });
      },
    );
    return () => controller.abort();
  }, [api]);

  const products = load.status === 'loaded' ? load.products : [];
  const active = products.filter((p) => p.status === 'active');
  const productId = chosen ?? active[0]?.id ?? '';
  const names = new Map(products.map((p) => [p.id, p.name]));

  const issue = async () => {
    setIssuing(true);
    setProblems(none);
    const sent = toProvenance(provenance);
    try {
      const batch = await issueBatch(api, {
        productId,
        quantity: toQuantity(quantity),
        ...(sent !== undefined ? { provenance: sent } : {}),
      });
      setLoad((current) =>
        current.status === 'loaded'
          ? { ...current, batches: [batch, ...current.batches] }
          : current,
      );
      setQuantity('');
      setProvenance(emptyProvenance);
    } catch (error) {
      setProblems(fromFields(error));
    }
    setIssuing(false);
  };

  const download = async (batch: CodeBatch, kind: ExportKind) => {
    setExporting({ batchId: batch.id, kind });
    setExportRefusal(null);
    try {
      saveFile(await exportBatch(api, batch.id, kind), exportFileName(batch, kind));
    } catch (error) {
      setExportRefusal(failureText(error));
    }
    setExporting(null);
  };

  const field = (
    key: keyof ProvenanceFields,
    label: string,
    kind: 'text' | 'datetime-local' | 'date' = 'text',
  ) => (
    <Field id={`batch-${key}`} label={label} message={problems.fields[key]}>
      <input
        id={`batch-${key}`}
        type={kind}
        value={provenance[key]}
        onChange={(e) => setProvenance((current) => ({ ...current, [key]: e.target.value }))}
        aria-invalid={problems.fields[key] !== undefined}
        style={input}
      />
    </Field>
  );

  const header = (
    <PageHeader
      title="Code batches"
      subtitle="One unique 128-bit code per physical unit: issued here, or pushed from your line"
    />
  );

  if (load.status === 'failed') {
    return (
      <>
        {header}
        <Card title="The code batches could not be loaded" body={failureText(load.error)} />
      </>
    );
  }

  const batches = load.status === 'loaded' ? load.batches : [];

  return (
    <>
      {load.status === 'loading' ? <Waiting>Loading code batches</Waiting> : null}
      {header}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '400px 1fr',
          gap: 16,
          flexGrow: 1,
          alignItems: 'start',
        }}
      >
        <div style={column}>
          {isAdmin ? (
            <form
              aria-label="Issue a batch"
              onSubmit={(e) => {
                e.preventDefault();
                void issue();
              }}
              style={cardBox}
            >
              <div style={{ padding: 22, display: 'flex', flexDirection: 'column', gap: 14 }}>
                <h2 style={{ ...type.panelTitle, color: colors.navy, margin: 0 }}>Issue a batch</h2>
                {problems.general !== undefined ? <Rejection>{problems.general}</Rejection> : null}
                <Field id="batch-product" label="Product" message={problems.fields.productId}>
                  <div style={{ position: 'relative' }}>
                    <select
                      id="batch-product"
                      value={productId}
                      onChange={(e) => setChosen(e.target.value)}
                      aria-invalid={problems.fields.productId !== undefined}
                      style={{ ...input, appearance: 'none', paddingRight: 36, cursor: 'pointer' }}
                    >
                      {active.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                    </select>
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      aria-hidden="true"
                      style={{ position: 'absolute', right: 14, top: 15, pointerEvents: 'none' }}
                    >
                      <path
                        d="M6 9 L12 15 L18 9"
                        stroke={colors.mutedInk}
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                  </div>
                </Field>
                <Field id="batch-quantity" label="Quantity" message={problems.fields.quantity}>
                  <input
                    id="batch-quantity"
                    inputMode="numeric"
                    value={quantity}
                    onChange={(e) => setQuantity(e.target.value)}
                    aria-invalid={problems.fields.quantity !== undefined}
                    style={input}
                  />
                </Field>

                <div
                  style={{
                    borderTop: `1px solid ${colors.line}`,
                    paddingTop: 14,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 12,
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <h3 style={{ ...type.button, color: colors.navy, margin: 0 }}>
                      What the line knew
                    </h3>
                    <span style={chipStyle(chip.neutral)}>Returned on every scan</span>
                  </div>
                  <div style={note}>
                    Held in the registry, not printed in the code, so it cannot be forged, and the
                    QR stays small enough for a can.
                  </div>
                  <div style={pair}>
                    {field('batchNumber', 'Batch / lot no.')}
                    {field('shift', 'Shift')}
                  </div>
                  <div style={pair}>
                    {field('manufacturedAt', 'Manufactured', 'datetime-local')}
                    {field('expiresAt', 'Expires', 'date')}
                  </div>
                  <div style={pair}>
                    {field('productionLine', 'Production line')}
                    {field('facility', 'Facility')}
                  </div>
                  <div style={note}>
                    Batch, manufactured and expires are{' '}
                    <span style={{ fontWeight: 700, color: verdict.suspect.fg }}>
                      required for regulated products
                    </span>
                    . Once issued, none of this can be edited: corrections mean a new batch.
                  </div>
                </div>

                <button
                  className="vp-fill"
                  type="submit"
                  disabled={issuing || load.status !== 'loaded' || productId === ''}
                  aria-busy={issuing}
                  style={issueButton}
                >
                  Issue batch
                </button>
              </div>
            </form>
          ) : (
            <Card
              title="Issuing a batch is for admins"
              body="Only organization admins can issue code batches. Every member can see them and export them."
            />
          )}
        </div>

        <div style={column}>
          {exportRefusal !== null ? <Rejection>{exportRefusal}</Rejection> : null}
          <div
            role="table"
            aria-label="Code batches"
            aria-busy={load.status === 'loading'}
            style={cardBox}
          >
            <div role="row" style={{ ...grid, borderBottom: `1px solid ${colors.line}` }}>
              {['Batch', 'Product', 'Source', 'Quantity', 'Created'].map((h) => (
                <div
                  key={h}
                  role="columnheader"
                  style={{ ...type.tableHeader, color: colors.mutedInk }}
                >
                  {h}
                </div>
              ))}
              <div role="columnheader" aria-label="Action" />
            </div>
            {batches.map((batch, i) => (
              <div key={batch.id}>
                {i > 0 ? <div style={{ height: 1, background: colors.line }} /> : null}
                <div role="row" style={{ ...grid, alignItems: 'center' }}>
                  <div role="cell" style={{ ...type.bodyStrong, color: colors.navy }}>
                    {batchCell(batch)}
                  </div>
                  <div role="cell" style={muted}>
                    {names.get(batch.productId) ?? ''}
                  </div>
                  <div role="cell">
                    <span style={chipStyle(sourceTone[batch.source] ?? chip.neutral)}>
                      {sourceLabels[batch.source] ?? batch.source}
                    </span>
                  </div>
                  <div role="cell" style={muted}>
                    {quantityCell(batch)}
                  </div>
                  <div role="cell" style={muted}>
                    {fullDate(new Date(batch.createdAt))}
                  </div>
                  <div role="cell">
                    {batch.source === 'demo' ? null : (
                      <ExportMenu
                        name={batchCell(batch) || names.get(batch.productId) || 'this batch'}
                        busy={exporting?.batchId === batch.id ? exporting.kind : null}
                        disabled={exporting !== null}
                        onPick={(kind) => void download(batch, kind)}
                      />
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <section aria-label="From your production line" style={cardBox}>
            <div style={{ padding: '20px 22px', display: 'flex', alignItems: 'center', gap: 20 }}>
              <div style={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                <h2 style={{ ...type.cardTitle, color: colors.navy, margin: 0 }}>
                  From your production line
                </h2>
                <div style={{ ...type.meta, color: colors.mutedInk, lineHeight: 1.5 }}>
                  Your ERP or line controller can push its own serials: each gets a Veriplus code
                  back to print, with the same batch details attached.
                </div>
                <div>
                  <button
                    className="vp-link"
                    type="button"
                    onClick={() => navigate('/api-keys')}
                    style={linkButton}
                  >
                    Manage API keys →
                  </button>
                </div>
              </div>
              <div data-part="machine-sample" style={sample}>
                POST /v1/machine/code-batches
                <br />
                X-Api-Key: vpk_9f3a2c1b…
              </div>
            </div>
          </section>
        </div>
      </div>
    </>
  );
}

function Field({
  id,
  label,
  message,
  children,
}: {
  id: string;
  label: string;
  message: string | undefined;
  children: ReactNode;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
      <label htmlFor={id} style={{ ...type.statLabel, color: colors.mutedInk }}>
        {label}
      </label>
      {children}
      {message !== undefined ? (
        <div role="alert" style={{ ...type.caption, color: verdict.unknown.fg }}>
          {message}
        </div>
      ) : null}
    </div>
  );
}

/** Hands the API's bytes to the browser as a download. */
/**
 * Not drawn, D34: one Export link per row opening the four files, the account
 * menu's pattern (D28) with arrow keys across its items. While a file is
 * prepared the row says which, and every row's link is disabled, as the CSV
 * link was. A demo row has none.
 */
function ExportMenu({
  name,
  busy,
  disabled,
  onPick,
}: {
  name: string;
  busy: ExportKind | null;
  disabled: boolean;
  onPick: (kind: ExportKind) => void;
}) {
  const [open, setOpen] = useState(false);
  const wrapper = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const items = useRef<(HTMLButtonElement | null)[]>([]);

  useEffect(() => {
    if (!open) return;
    items.current[0]?.focus();
    const away = (event: MouseEvent) => {
      if (!wrapper.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, [open]);

  function onMenuKey(event: KeyboardEvent) {
    const at = items.current.findIndex((item) => item === document.activeElement);
    const last = exportKinds.length - 1;
    const to: Record<string, number> = {
      ArrowDown: at === last ? 0 : at + 1,
      ArrowUp: at <= 0 ? last : at - 1,
      Home: 0,
      End: last,
    };
    if (event.key === 'Escape') {
      event.preventDefault();
      setOpen(false);
      button.current?.focus();
    } else if (event.key === 'Tab') {
      setOpen(false);
    } else if (event.key in to) {
      event.preventDefault();
      items.current[to[event.key]!]?.focus();
    }
  }

  if (busy !== null) {
    return (
      <span role="status" style={{ ...type.metaBold, color: colors.mutedInk }}>
        Preparing {exportFormat(busy)}…
      </span>
    );
  }

  return (
    <div ref={wrapper} style={{ position: 'relative' }}>
      <button
        className="vp-link"
        ref={button}
        type="button"
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Export ${name}`}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            setOpen(true);
          }
        }}
        style={linkButton}
      >
        Export ▾
      </button>
      {open ? (
        <div role="menu" aria-label={`Export ${name}`} onKeyDown={onMenuKey} style={menuBox}>
          {exportKinds.map((kind, i) => (
            <button
              key={kind}
              className="vp-outline"
              ref={(item) => {
                items.current[i] = item;
              }}
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                onPick(kind);
              }}
              style={menuItem}
            >
              {exportLabels[kind]}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/** The account menu's values (D28), opening below the link and aligned to its start. */
const menuBox: CSSProperties = {
  position: 'absolute',
  left: 0,
  top: '100%',
  marginTop: 8,
  zIndex: 1,
  minWidth: 160,
  background: colors.card,
  border: `1px solid ${colors.line}`,
  borderRadius: shell.navRow.radius,
  padding: 4,
};

const menuItem: CSSProperties = {
  display: 'block',
  width: '100%',
  padding: `${shell.navRow.paddingY}px ${shell.navRow.paddingX}px`,
  borderRadius: shell.navRow.radius - 4,
  border: 'none',
  background: 'transparent',
  textAlign: 'left',
  whiteSpace: 'nowrap',
  cursor: 'pointer',
  ...type.bodyStrong,
  color: colors.navy,
};

const drawn: ReadonlySet<string> = new Set<FieldKey>([
  'productId',
  'quantity',
  'batchNumber',
  'shift',
  'manufacturedAt',
  'expiresAt',
  'productionLine',
  'facility',
]);

/**
 * Where an issue failure lands. `validate` keys by `body.<path>` and the
 * readiness gate by `provenance.<field>` or `product.<field>`: a drawn field
 * takes its message, anything else, or a failure with no details, goes to the
 * box at the top of the card. D10's rule, D19.
 */
function fromFields(error: unknown): Problems {
  const problems: Problems = { fields: {} };
  if (!(error instanceof ApiError) || error.details === undefined) {
    problems.general = failureText(error);
    return problems;
  }
  const rest: string[] = [];
  for (const [path, message] of Object.entries(error.details)) {
    const field = path.replace(/^body\./, '').replace(/^provenance\./, '');
    if (drawn.has(field)) problems.fields[field as FieldKey] = message;
    else rest.push(message);
  }
  if (rest.length > 0 || error.code === 'not_ready_to_issue') {
    problems.general = [error.code === 'not_ready_to_issue' ? error.message : null, ...rest]
      .filter(Boolean)
      .join(' ');
  }
  return problems;
}

function failureText(error: unknown): string {
  if (error instanceof ApiError && error.kind === 'network') {
    return 'The API could not be reached. Reload the page to try again.';
  }
  if (error instanceof Error) return error.message;
  return 'Reload the page to try again.';
}

const column: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0 };

const cardBox: CSSProperties = {
  background: colors.card,
  border: `1px solid ${colors.line}`,
  borderRadius: radius.card,
};

const grid: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: columns,
  gap: 14,
  padding: '14px 20px',
};

const pair: CSSProperties = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 };

const note: CSSProperties = { ...type.caption, color: colors.mutedInk, lineHeight: 1.5 };

const muted: CSSProperties = { ...type.body, color: colors.mutedInk };

const input: CSSProperties = {
  height: 44,
  width: '100%',
  boxSizing: 'border-box',
  borderRadius: 12,
  background: colors.card,
  border: `1px solid ${colors.line}`,
  padding: '0 14px',
  ...type.body,
  color: colors.navy,
};

const chipStyle = (tone: { bg: string; fg: string }): CSSProperties => ({
  display: 'inline-flex',
  padding: '4px 12px',
  borderRadius: radius.chip,
  background: tone.bg,
  color: tone.fg,
  ...type.captionBold,
});

const linkButton: CSSProperties = {
  ...type.metaBold,
  color: colors.brand,
  background: 'none',
  border: 'none',
  padding: 0,
  cursor: 'pointer',
};

/** The board draws it as a full-width row with its label at the start, not centred. */
const issueButton: CSSProperties = {
  height: shell.actionButton.height,
  padding: `0 ${shell.actionButton.paddingX}px`,
  borderRadius: shell.actionButton.radius,
  background: colors.brand,
  color: colors.white,
  border: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  cursor: 'pointer',
  ...type.button,
};

const sample: CSSProperties = {
  borderRadius: 12,
  background: colors.scannerSurface,
  padding: '14px 16px',
  fontFamily: fonts.mono,
  fontSize: '12.5px',
  color: sampleInk,
  lineHeight: 1.7,
  flexShrink: 0,
};
